import {
  Button,
  Copy,
  Flex,
  LegacyForm,
  Modal,
  Select,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { CheckCircleFilled } from "@payping/icons";
import { OtpCode } from "@/components/OtpCode";
import { useState } from "react";
import { clientApis } from "@payping/api";
import { isLocalIp, useAppTranslation, validIpOrRange } from "@/utils";
import { pluginsListList } from "./data";

interface ModalProps {
  visible?: boolean;
  onClose: () => void;
  pluginKey: string;
}
interface FormType {
  allowedIPs?: string[];
}
const GetTokenModal = (props: ModalProps) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FormType>();
  const [isOtpValid, setIsOtpValid] = useState(false);

  const [{ loading, value: createdToken }, handleCreate] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { serviceType } = pluginsListList[props.pluginKey];
      if (serviceType) {
        const value = await clientApis.createAccessTokenWithRequestModelPost({
          allowedIPs: values.allowedIPs,
          pluginType: serviceType,
        });

        return value.token;
      }
      return undefined;
    },
    [props.pluginKey],
  );

  return (
    <Modal footer={null} open={props.visible} onCancel={() => props.onClose()} title={t("connections.token.addToken")}>
      {createdToken ? (
        <div>
          <Flex align="center" vertical gap="0.5rem" className="tw:mb-4!">
            <CheckCircleFilled className="color--success" width={70} />
            <Typography.Title level={5}>{t("connections.token.successfulCreated.successMessage")}</Typography.Title>
          </Flex>
          <Flex gap="1rem" vertical className="background--gray100 tw:p-4!">
            <Typography.Text strong>{t("connections.token.successfulCreated.tokenAttention")}</Typography.Text>
            <Flex vertical gap="0.5rem">
              <Typography.Text>{t("connections.token.token")}</Typography.Text>
              <Copy text={createdToken} showIcon isButton>
                {`${createdToken.slice(0, 30)}...`}
              </Copy>
            </Flex>
          </Flex>
        </div>
      ) : (
        <LegacyForm<FormType> form={form} onFinish={handleCreate} layout={"vertical"}>
          <LegacyForm.Item<FormType>
            label={t("token.allowedIps")}
            name="allowedIPs"
            dependencies={["scopes"]}
            tooltip={t("token.allowedIpsHelp")}
            rules={[
              { required: true },
              () => ({
                validator(_rule, value: string[]): Promise<void> {
                  const invalidIp = (value || []).find((ip) => !validIpOrRange(ip));
                  if (invalidIp) {
                    return Promise.reject(t("error.ip"));
                  }
                  const localIp = (value || []).find((ip) => isLocalIp(ip));
                  return localIp ? Promise.reject(t("error.localIp")) : Promise.resolve();
                },
              }),
            ]}
          >
            <Select className={"tw:dir-ltr!"} placeholder={"8.8.8.8"} mode="tags" />
          </LegacyForm.Item>
          <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
          <Space className="justify-end">
            <Button onClick={() => props.onClose()} disabled={loading}>
              {t("back")}
            </Button>
            <Button htmlType="submit" type="primary" loading={loading} disabled={!isOtpValid}>
              {t("connections.token.addToken")}
            </Button>
          </Space>
        </LegacyForm>
      )}
    </Modal>
  );
};

export default GetTokenModal;
